import { useLocation } from "wouter";
import { PageBackground } from "@/components/ui/page-background";
import { Card } from "@/components/ui/card";
import { Calendar, Music, ArrowRight } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";

export default function ManagementSelection() {
  const [, setLocation] = useLocation();
  const { user } = useAuth();

  return (
    <PageBackground>
      <div className="min-h-screen flex items-center justify-center px-4 py-8">
        <div className="max-w-4xl w-full">
          {/* Header */}
          <div className="text-center mb-10 mt-10">
            <h1 className="text-4xl font-bold text-slate-900 mb-3 drop-shadow-sm">
              Welcome back, {user?.username}!
            </h1>
          </div>

          {/* Selection Cards */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Event Management Card */}
            <Card
              className="bg-white/20 backdrop-blur-xl rounded-2xl border border-white/30 hover:bg-white/30 transition-all duration-300 cursor-pointer group hover:scale-[1.02] relative overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.1) 100%)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.4)',
              }}
              onClick={() => setLocation('/event-management')}
            >
              {/* Specular highlight on hover */}
              <div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, transparent 50%, rgba(255,255,255,0.1) 100%)',
                }}
              />

              <div className="p-8 relative z-10">
                <div className="flex flex-col items-center text-center space-y-4">
                  <div className="w-20 h-20 bg-blue-600/20 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-blue-600/30 group-hover:bg-blue-600/30 transition-colors">
                    <Calendar className="h-10 w-10 text-blue-600" />
                  </div>

                  <div>
                    <h2 className="text-2xl font-semibold text-slate-900 mb-2">
                      Event Management
                    </h2>
                    <p className="text-slate-600 mb-4">
                      Manage jam nights, open mics, and live events. Track participants, handle staging, and run your venues.
                    </p>

                    <div className="flex flex-col gap-2 text-sm text-slate-500">
                      <div className="flex items-center justify-center gap-2">
                        <span className="w-1.5 h-1.5 bg-blue-600 rounded-full"></span>
                        <span>Participant Management</span>
                      </div>
                      <div className="flex items-center justify-center gap-2">
                        <span className="w-1.5 h-1.5 bg-blue-600 rounded-full"></span>
                        <span>Stage Rotation</span>
                      </div>
                      <div className="flex items-center justify-center gap-2">
                        <span className="w-1.5 h-1.5 bg-blue-600 rounded-full"></span>
                        <span>Venue Operations</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 text-blue-600 font-medium group-hover:gap-3 transition-all">
                    <span>Open Event Manager</span>
                    <ArrowRight className="h-4 w-4" />
                  </div>
                </div>
              </div>
            </Card>

            {/* Booking Management Card */}
            <Card
              className="bg-white/20 backdrop-blur-xl rounded-2xl border border-white/30 hover:bg-white/30 transition-all duration-300 cursor-pointer group hover:scale-[1.02] relative overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.1) 100%)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.4)',
              }}
              onClick={() => setLocation('/booking-management')}
            >
              {/* Specular highlight on hover */}
              <div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, transparent 50%, rgba(255,255,255,0.1) 100%)',
                }}
              />

              <div className="p-8 relative z-10">
                <div className="flex flex-col items-center text-center space-y-4">
                  <div className="w-20 h-20 bg-purple-600/20 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-purple-600/30 group-hover:bg-purple-600/30 transition-colors">
                    <Music className="h-10 w-10 text-purple-600" />
                  </div>

                  <div>
                    <h2 className="text-2xl font-semibold text-slate-900 mb-2">
                      Booking Management
                    </h2>
                    <p className="text-slate-600 mb-4">
                      Manage your artists, acts, and bookings.
                    </p>

                    <div className="flex flex-col gap-2 text-sm text-slate-500">
                      <div className="flex items-center justify-center gap-2">
                        <span className="w-1.5 h-1.5 bg-purple-600 rounded-full"></span>
                        <span>Artist Roster</span>
                      </div>
                      <div className="flex items-center justify-center gap-2">
                        <span className="w-1.5 h-1.5 bg-purple-600 rounded-full"></span>
                        <span>Booking Calendar</span>
                      </div>
                      <div className="flex items-center justify-center gap-2">
                        <span className="w-1.5 h-1.5 bg-purple-600 rounded-full"></span>
                        <span>Financial Tracking</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 text-purple-600 font-medium group-hover:gap-3 transition-all">
                    <span>Open Booking Manager</span>
                    <ArrowRight className="h-4 w-4" />
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Quick Actions */}
          <div className="mt-8 text-center">
            <p className="text-sm text-slate-500">
              You can switch between management modes at any time from the main menu
            </p>
          </div>
        </div>
      </div>
    </PageBackground>
  );
}